import { Link, useNavigate, useParams } from "@tanstack/react-router";

import {
  useArchiveReservoir,
  useReservoir,
} from "@/lib/api/reservoirs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState } from "@/components/error-state";
import { AuditLogViewer } from "@/components/audit-log-viewer";

export function ReservoirDetailPage() {
  const { reservoirId } = useParams({ from: "/reservoirs/$reservoirId" });
  const navigate = useNavigate();
  const q = useReservoir(reservoirId);
  const archive = useArchiveReservoir(reservoirId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  const r = q.data;

  // Cylindrical containers (round buckets) have no length/width — the schema
  // models only L×W×H, so a round container's diameter is recoverable from its
  // cross-section (area = π·r²). Show Ø instead of two empty L/W rows.
  const isRound =
    r.inside_l_in === undefined &&
    r.inside_w_in === undefined &&
    r.cross_section_in2 !== undefined;
  const dimensionRows: [string, number | undefined][] = isRound
    ? [
        ["Ø", Number((2 * Math.sqrt(r.cross_section_in2! / Math.PI)).toFixed(2))],
        ["H", r.inside_h_in],
      ]
    : [
        ["L", r.inside_l_in],
        ["W", r.inside_w_in],
        ["H", r.inside_h_in],
      ];

  function onArchive() {
    const reason =
      window.prompt("Reason for archiving?") || "user archive";
    archive.mutate(reason, {
      onSuccess: () => navigate({ to: "/reservoirs" }),
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{r.name}</h1>
          <p className="text-sm text-muted-foreground">
            {r.container_type}
            {r.system_provided && " · system-provided"}
            {r.archived_at && " · archived"}
          </p>
        </div>
        {!r.system_provided && !r.archived_at && (
          <div className="flex gap-2">
            <Link
              to="/reservoirs/$reservoirId/edit"
              params={{ reservoirId: r.id }}
            >
              <Button variant="outline">Edit</Button>
            </Link>
            <Button
              variant="ghost"
              onClick={onArchive}
              disabled={archive.isPending}
            >
              {archive.isPending ? "Archiving…" : "Archive"}
            </Button>
          </div>
        )}
      </header>

      <section className="grid gap-4 md:grid-cols-3">
        <DimensionCard title="Inside dimensions (in)" rows={dimensionRows} />
        <DimensionCard
          title="Conversion"
          rows={[
            ["cross_section_in²", r.cross_section_in2],
            ["gal_per_inch", r.gal_per_inch],
            ["cm_per_gal", r.cm_per_gal],
          ]}
        />
        <DimensionCard
          title="Notes"
          notes={r.notes}
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Audit trail
        </h2>
        <AuditLogViewer entityType="reservoir" entityID={r.id} />
      </section>
    </div>
  );
}

function DimensionCard({
  title,
  rows,
  notes,
}: {
  title: string;
  rows?: [string, number | undefined][];
  notes?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {rows?.map(([label, v]) => (
          <div key={label} className="flex justify-between gap-4">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums">{v ?? "—"}</span>
          </div>
        ))}
        {notes !== undefined && (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">
            {notes || "—"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
